(function () {
  class StickySectionNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.sticky-section-nav__bar');
      this.placeholder = this.querySelector('.sticky-section-nav__placeholder');
      this.links = Array.from(this.querySelectorAll('.sticky-section-nav__link'));
      this.targets = this._resolveTargets();
      this.isClickScrolling = false;
      this.clickScrollTimer = null;
      this.activeIndex = 0;

      if (!this.bar || this.links.length === 0) return;

      this._onScroll = this._onScroll.bind(this);
      this._onScrollEnd = this._onScrollEnd.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onLinkClick = this._onLinkClick.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this._onLinkClick));
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('scrollend', this._onScrollEnd);
      window.addEventListener('resize', this._onResize, { passive: true });

      this._onResize();
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('scrollend', this._onScrollEnd);
      window.removeEventListener('resize', this._onResize);
      this.links.forEach((link) => link.removeEventListener('click', this._onLinkClick));
      clearTimeout(this.clickScrollTimer);
    }

    _resolveTargets() {
      return this.links
        .map((link) => {
          const anchorId = link.dataset.anchor;
          if (!anchorId) return null;

          return (
            document.getElementById(anchorId) ||
            document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
            document.getElementById(`shopify-section-${anchorId}`)
          );
        })
        .filter(Boolean);
    }

    _readPxVar(name) {
      const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
      return Number.isFinite(value) ? value : 0;
    }

    _getHeaderBottom() {
      const headerVisible = this._readPxVar('--header-is-visible');
      if (headerVisible === 0) return 0;

      const headerGroup = document.querySelector('.shopify-section-group-header-group');
      if (headerGroup) {
        let bottom = 0;

        headerGroup.querySelectorAll('.shopify-section').forEach((section) => {
          const rect = section.getBoundingClientRect();
          if (rect.height > 0) {
            bottom = Math.max(bottom, rect.bottom);
          }
        });

        if (bottom > 0) return bottom;
      }

      const headerObserver = document.querySelector('height-observer[variable="header"]');
      if (headerObserver) {
        return headerObserver.getBoundingClientRect().bottom;
      }

      return 0;
    }

    _getStickyOffset() {
      const measuredBottom = this._getHeaderBottom();
      if (measuredBottom > 0) return measuredBottom;

      const headerVisible = this._readPxVar('--header-is-visible') || 1;
      const headerHeight = this._readPxVar('--header-height-live');

      return headerVisible * headerHeight;
    }

    _getNavHeight() {
      return this.bar ? this.bar.offsetHeight : 0;
    }

    _getScrollOffset() {
      return this._getStickyOffset() + this._getNavHeight();
    }

    _onResize() {
      if (this.bar.classList.contains('is-fixed')) {
        this.placeholder.style.height = `${this._getNavHeight()}px`;
      }
    }

    _onScroll() {
      this._updateFixedState();
      if (!this.isClickScrolling) {
        this._updateActiveLink();
      }
    }

    _onScrollEnd() {
      if (this.isClickScrolling) {
        this.isClickScrolling = false;
        clearTimeout(this.clickScrollTimer);
        this._updateActiveLink();
      }
    }

    _updateFixedState() {
      const stickyOffset = this._getStickyOffset();
      const sectionTop = this.getBoundingClientRect().top + window.scrollY;
      const shouldFix = window.scrollY + stickyOffset >= sectionTop;

      if (shouldFix) {
        if (!this.bar.classList.contains('is-fixed')) {
          this.placeholder.style.height = `${this._getNavHeight()}px`;
          this.placeholder.style.display = 'block';
          this.bar.classList.add('is-fixed');
        }

        this.bar.style.top = `${this._getStickyOffset()}px`;
      } else {
        this.bar.classList.remove('is-fixed');
        this.bar.style.removeProperty('top');
        this.placeholder.style.display = 'none';
        this.placeholder.style.height = '0';
      }
    }

    _updateActiveLink() {
      const offset = this._getScrollOffset();
      const activationTolerance = 2;
      let activeIndex = 0;
      let bestTop = -Infinity;

      this.links.forEach((link, index) => {
        const anchorId = link.dataset.anchor;
        const target =
          document.getElementById(anchorId) ||
          document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
          document.getElementById(`shopify-section-${anchorId}`);

        if (!target) return;

        const targetTop = target.getBoundingClientRect().top;
        if (targetTop <= offset + activationTolerance && targetTop > bestTop) {
          bestTop = targetTop;
          activeIndex = index;
        }
      });

      const indexChanged = this.activeIndex !== activeIndex;
      this.activeIndex = activeIndex;

      this.links.forEach((link, index) => {
        const isActive = index === activeIndex;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (indexChanged) {
        this._scrollActiveLinkIntoView();
      }
    }

    _scrollActiveLinkIntoView() {
      const activeLink = this.querySelector('.sticky-section-nav__link.is-active');
      if (activeLink) {
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    _onLinkClick(event) {
      event.preventDefault();

      const link = event.currentTarget;
      const anchorId = link.dataset.anchor;
      const target =
        document.getElementById(anchorId) ||
        document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
        document.getElementById(`shopify-section-${anchorId}`);

      if (!target) return;

      const activeIndex = this.links.indexOf(link);
      this.activeIndex = activeIndex;

      this.links.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-selected', 'false');
      });
      link.classList.add('is-active');
      link.setAttribute('aria-selected', 'true');
      this._scrollActiveLinkIntoView();

      this.isClickScrolling = true;
      clearTimeout(this.clickScrollTimer);

      const targetTop = target.getBoundingClientRect().top + window.scrollY - this._getScrollOffset();
      window.scrollTo({ top: targetTop, behavior: 'smooth' });

      this.clickScrollTimer = setTimeout(() => {
        if (this.isClickScrolling) {
          this.isClickScrolling = false;
          this._updateActiveLink();
        }
      }, 1500);
    }
  }

  if (!window.customElements.get('sticky-section-nav')) {
    window.customElements.define('sticky-section-nav', StickySectionNav);
  }
})();
